import React, { useEffect } from 'react';
import { useLoaderData, useParams } from '@tanstack/react-router';
import { patchSubmission } from '../../api/submissions/patch-submission';
import { filterEncryptedInputValues, commitEncryptedInputEdits } from '../survey-questions/encrypted-input';
import {
  UserType, SIGNED_SUBMISSION_STATUSES,
} from '../../types';
import { getSurveyModel } from '../survey-questions/get-survey-model';
import Survey from '../survey/survey';
import { CurrentPageChangingEvent, SurveyModel } from 'survey-core';
import { signSubmission } from '../../api/submissions/sign-submission';
import { SubmissionLoaderResponse } from '../../utils/submission-route-loader';
import {
  isApiError,
  isUnauthorizedError,
  isExpiredToken,
  isNotFoundError,
  isBadRequestError,
} from '../../utils/errors';
import { displayCustomPage, IS_CUSTOM_PAGE } from '../../utils/custom-page/display-custom-page';
import { EmailConfirmationMessage } from '../email-confirmation-message/email-confirmation-message';
import { TokenExpirationMessage } from '../token-expiration-message/token-expiration-message';
import { useStatusMessageDispatch } from '../../hooks/StatusMessageContext';
import i18n from '../../i18n';
import ErrorPage from '../status-pages/error-page';
import { setDatadogUser } from '../../utils/datadog-user-tracking';

const SurveySubmission: React.FC = () => {
  const {
    settings,
    submission,
    error,
  } = useLoaderData({ from: '/$accountSlug/$submissionId/' }) as SubmissionLoaderResponse;

  const lockSubmission = !submission || !!error || !!submission.signer || SIGNED_SUBMISSION_STATUSES.has(submission.status);
  const { setStatusMessage } = useStatusMessageDispatch();
  const { submissionId } = useParams({ from: '/$accountSlug/$submissionId/' });

  // Track user in Datadog when submission is loaded
  useEffect(() => {
    if (submission) {
      setDatadogUser(submission);
    }
  }, [ submission ]);

  if (isNotFoundError(error) || isBadRequestError(error)) {
    return <ErrorPage
      errorType={error}
      link={`/${settings.urlSlug}/`}
           />;
  }

  const handleSubmission = async (sender: SurveyModel) => {
    // After the signer has verified their email, the initial user can't update the submission
    if (lockSubmission) return;

    await signSubmission(settings.urlSlug, submission.publicId, filterEncryptedInputValues(sender.data, sender), UserType.INITIAL_USER);
  };

  const handleSave = async (sender: SurveyModel, options: CurrentPageChangingEvent) => {
    if (lockSubmission) return;

    // Do not save invalid data
    if (!options.oldCurrentPage?.validate(false)) return; // Allows preview page to be used (`showPreviewBeforeComplete=true`)
    // if custom page is added do not execute after triggering the CurrentPageChangingEvent
    if (options.newCurrentPage.getPropertyValue(IS_CUSTOM_PAGE)) return;
    try {
      await patchSubmission(settings.urlSlug, submission.publicId, filterEncryptedInputValues(sender.data, sender), UserType.INITIAL_USER);
      commitEncryptedInputEdits(sender);
    } catch (error) {
      if (isApiError(error) && isExpiredToken(error)) {
        displayCustomPage({
          survey: surveyModel,
          component: TokenExpirationMessage,
          getProps: () => ({
            publicId: submissionId,
            slug: settings.urlSlug,
          }),
          withoutFrame: true,
        });

        sender.readOnly = true;
      } else {
        setStatusMessage({
          severity: 'error',
          message: isApiError(error) ? error.message : 'Failed to save.',
        });
      }
    }
  };

  const surveyModel = getSurveyModel({
    settings,
    submission,
    readOnly: lockSubmission,
    onComplete: handleSubmission,
    onCurrentPageChanging: handleSave,
    completedHtml: i18n.t('components:pages.application_submitted'),
    currentPage: 1,
  });

  // This route is only used after token validation and redirect, so we only show email confirmation
  // (not token expiration, which is handled by the token route)
  if (isUnauthorizedError(error)) {
    const component = isExpiredToken(error) ? TokenExpirationMessage : EmailConfirmationMessage;
    displayCustomPage({
      survey: surveyModel,
      component,
      getProps: () => ({
        publicId: submissionId,
        slug: settings.urlSlug,
      }),
      withoutFrame: true,
    });

    // Remove all pages except custom page
    const customPage = surveyModel.getPageByName(component.name);
    surveyModel.pages.slice().forEach((page) => {
      if (page !== customPage) {
        surveyModel.removePage(page);
      }
    });
    surveyModel.showProgressBar = 'off';
    surveyModel.showTOC = false;
    surveyModel.showNavigationButtons = false;
  }

  return (
    <Survey
      surveyModel={surveyModel}
    />
  );
};

export default SurveySubmission;
